import React from 'react';
import { Box, Typography, Card, CardContent, styled } from '@mui/material';
import icon1 from './img/icon1.svg';
import icon2 from './img/icon3.svg';
import icon3 from './img/icon4.png';
import icon4 from './img/icon5.png';
import icon5 from './img/dataCollectionIcon1.png';
import icon6 from './img/icon6.png';

// Styled Card component with animations and gradients
const StyledCard = styled(Card)(({ theme, bgColor }) => ({
    background: bgColor,
    borderRadius: '16px',
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    cursor: 'pointer',
    width: '100%', // Ensure equal width
    maxWidth: '380px', // Fixed max width for consistency
    minHeight: '260px', // Fixed min height for uniformity
    display: 'flex', // Ensure content stretches properly
    flexDirection: 'column',
    justifyContent: 'space-between',
    '&:hover': {
        transform: 'translateY(-10px)',
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    },
    [theme.breakpoints.down('sm')]: {
        margin: '10px 0',
        maxWidth: '100%', // Full width on small screens
        minHeight: '240px', // Slightly smaller height for mobile
    },
}));

const CardMenuSection = () => {
    return (
        <Box
            sx={{
                padding: { xs: '20px', md: '40px' },
                backgroundColor: '#f5f7fa',
                textAlign: 'center',
                position: 'relative',
            }}
        >
            {/* Title */}
            <Typography
                variant="h2"
                sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.2,
                    mb: 3,
                }}
            >
                POWERFUL BENEFITS OF <span style={{ color: '#00D084' }}>WHATSAPP</span> WEBVIEWS
            </Typography>

            {/* Cards Container (First Row) */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'center',
                    gap: '20px',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    marginBottom: '40px', // Space between rows
                }}
            >
                {/* Card 1 */}
                <StyledCard bgColor="linear-gradient(135deg, #e6faff, #c6f0fa)">
                    <CardContent sx={{ textAlign: 'left', padding: '30px', flexGrow: 1 }}>
                        <Box
                            component="img"
                            src={icon1}
                            alt="Smile Icon"
                            sx={{ width: '40px', height: '40px', marginBottom: '20px' }}
                        />
                        <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
                            Enhanced User Experience
                        </Typography>
                        <Typography variant="body2" color="textSecondary" fontSize="16px">
                            By keeping the process within WhatsApp, users enjoy a much better & faster experience, improving engagement and conversions.
                        </Typography>
                    </CardContent>
                </StyledCard>

                {/* Card 2 */}
                <StyledCard bgColor="linear-gradient(135deg, #e0f5e5, #c3edd0)">
                    <CardContent sx={{ textAlign: 'left', padding: '30px', flexGrow: 1 }}>
                        <Box
                            component="img"
                            src={icon2}
                            alt="Arrow Up Icon"
                            sx={{ width: '40px', height: '40px', marginBottom: '20px' }}
                        />
                        <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
                            Increased Conversion Rates
                        </Typography>
                        <Typography variant="body2" color="textSecondary" fontSize="16px">
                            You minimise drop-offs and boost conversions by enabling customers to complete actions like purchases or bookings directly within WhatsApp.
                        </Typography>
                    </CardContent>
                </StyledCard>

                {/* Card 3 */}
                <StyledCard bgColor="linear-gradient(135deg, #fff7e6, #ffeedd)">
                    <CardContent sx={{ textAlign: 'left', padding: '30px', flexGrow: 1 }}>
                        <Box
                            component="img"
                            src={icon3}
                            alt="Plus Icon"
                            sx={{ width: '40px', height: '40px', marginBottom: '20px' }}
                        />
                        <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
                            Streamlined Customer Support
                        </Typography>
                        <Typography variant="body2" color="textSecondary" fontSize="16px">
                            Handle order tracking, issue resolutionand real-time conversations all within the WhatsApp interface, leading to faster responses and happier customers.
                        </Typography>
                    </CardContent>
                </StyledCard>
            </Box>

            {/* Cards Container (Second Row) */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'center',
                    gap: '20px',
                    maxWidth: '1200px',
                    margin: '0 auto',
                }}
            >
                {/* Card 4 */}
                <StyledCard bgColor="linear-gradient(135deg, #f3eefa, #e4d7f5)">
                    <CardContent sx={{ textAlign: 'left', padding: '30px', flexGrow: 1 }}>
                        <Box
                            component="img"
                            src={icon4}
                            alt="Smile Icon"
                            sx={{
                                width: '40px',
                                height: '40px',
                                marginBottom: '20px',
                                filter: 'brightness(0.7)', // Darken the icon slightly
                            }}
                        />
                        <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
                            Personalized Interactions
                        </Typography>
                        <Typography variant="body2" color="textSecondary" fontSize="16px">
                            Deliver tailored content and offers within the chat, enhancing user engagement and loyalty.
                        </Typography>
                    </CardContent>
                </StyledCard>

                {/* Card 5 */}
                <StyledCard bgColor="linear-gradient(135deg, #fce4ec, #f7d2e0)">
                    <CardContent sx={{ textAlign: 'left', padding: '30px', flexGrow: 1 }}>
                        <Box
                            component="img"
                            src={icon5}
                            alt="Arrow Up Icon"
                            sx={{
                                width: '40px',
                                height: '40px',
                                marginBottom: '20px',
                                filter: 'brightness(0.7)', // Darken the icon slightly
                            }}
                        />
                        <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
                            Simplified Data Collection
                        </Typography>
                        <Typography variant="body2" color="textSecondary" fontSize="16px">
                            Gather customer information seamlessly through forms or surveys within WhatsApp, making data collection more efficient.
                        </Typography>
                    </CardContent>
                </StyledCard>

                {/* Card 6 */}
                <StyledCard bgColor="linear-gradient(135deg, #d7f0ed, #c1e8e4)">
                    <CardContent sx={{ textAlign: 'left', padding: '30px', flexGrow: 1 }}>
                        <Box
                            component="img"
                            src={icon6}
                            alt="Plus Icon"
                            sx={{ width: '40px', height: '40px', marginBottom: '20px' }}
                        />
                        <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
                            Enhanced Security & Trust
                        </Typography>
                        <Typography variant="body2" color="textSecondary" fontSize="16px">
                            Leverage WhatsApp's secure environment to build trust with customers while maintaining end-to-end encryption for all interactions.
                        </Typography>
                    </CardContent>
                </StyledCard>
            </Box>
        </Box>
    );
};

export default CardMenuSection;